import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { CheckCircle2, Anchor, ArrowRight, Sparkles, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const SUPPORT_EMAIL = 'sailservicesa@gmail.com';

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/subscription/plans`).then(r => {
      setPlans(r.data.plans || []);
    }).catch(() => toast.error('Could not load plans'))
      .finally(() => setLoading(false));
  }, []);

  const subscribe = async (planId) => {
    if (planId === 'free') {
      navigate('/register');
      return;
    }
    if (!user) {
      // Send to register, with intent
      navigate(`/register?plan=${planId}`);
      return;
    }
    setPaying(planId);
    try {
      const r = await axios.post(`${API}/subscription/initialize`, { plan_id: planId, callback_path: '/billing/callback' }, { withCredentials: true });
      if (r.data?.authorization_url) {
        window.location.href = r.data.authorization_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start payment');
    } finally { setPaying(null); }
  };

  return (
    <div className="min-h-screen pt-10 pb-24">
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] flex items-center justify-center"><Anchor className="w-5 h-5 text-white" /></div>
          <div className="flex flex-col leading-none">
            <span className="font-bold tracking-tight text-lg text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>SAIL</span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-muted)] font-semibold">Services SA</span>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          {user ? (
            <Link to="/dashboard" className="text-sm font-semibold text-[var(--ocean-deep)] px-3 py-2">Dashboard →</Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--ocean-deep)] px-3 py-2">Sign in</Link>
              <Link to="/register" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white text-sm font-semibold">Start free trial</Link>
            </>
          )}
        </div>
      </nav>
      <div className="max-w-7xl mx-auto px-6 mt-10">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-[var(--border-glass)] text-xs font-semibold text-[var(--ocean-deep)] mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[var(--ocean-surface)]" /> 14-day free trial on all plans
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight text-[var(--ocean-deep)]" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            Simple, fair pricing.
          </h1>
          <p className="mt-4 text-lg text-[var(--text-secondary)]">
            Per user / month, billed in ZAR via Paystack. Cancel anytime. Start with a free 14-day trial — no card required.
          </p>
        </div>
        {loading ? (
          <p className="text-center mt-12 text-[var(--text-muted)]">Loading plans…</p>
        ) : (
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {plans.map(p => {
              const hl = p.id === 'professional';
              return (
                <div key={p.id} className={`relative rounded-3xl p-7 border flex flex-col ${hl ? 'bg-gradient-to-br from-[var(--ocean-deep)] to-[var(--ocean-surface)] text-white border-transparent shadow-2xl shadow-[var(--ocean-surface)]/30 scale-[1.02]' : 'bg-white/80 backdrop-blur-xl border-[var(--border-glass)]'}`}>
                  {hl && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[var(--ocean-coral)] text-white text-[10px] font-bold uppercase tracking-wider">Most popular</div>
                  )}
                  <p className={`text-xs font-bold uppercase tracking-wider ${hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>{p.name}</p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className={`text-5xl font-extrabold ${hl ? 'text-white' : 'text-[var(--ocean-deep)]'}`} style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>R{p.price.toLocaleString()}</span>
                    <span className={`text-sm ${hl ? 'text-white/70' : 'text-[var(--text-muted)]'}`}>/user/mo</span>
                  </div>
                  <p className={`mt-2 text-sm ${hl ? 'text-white/80' : 'text-[var(--text-secondary)]'}`}>{p.description}</p>
                  <ul className="mt-6 space-y-3 flex-1">
                    {(p.features || []).map(f => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hl ? 'text-[var(--ocean-shallow)]' : 'text-[var(--ocean-surface)]'}`} />
                        <span className={hl ? 'text-white/90' : 'text-[var(--text-primary)]'}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => subscribe(p.id)}
                    disabled={paying === p.id}
                    className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl font-semibold transition-all disabled:opacity-50 ${hl ? 'bg-white text-[var(--ocean-deep)] hover:bg-[var(--ocean-foam)]' : p.id === 'free' ? 'bg-[var(--ocean-foam)] text-[var(--ocean-deep)] hover:bg-[var(--ocean-shallow)]/30' : 'bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-mid)] text-white hover:-translate-y-0.5'}`}
                  >
                    {paying === p.id ? 'Loading…' : p.id === 'free' ? 'Get started' : user ? 'Subscribe' : 'Start trial'} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-16 max-w-2xl mx-auto text-center p-8 rounded-2xl bg-white/70 backdrop-blur-xl border border-[var(--border-glass)]">
          <h3 className="text-xl font-bold text-[var(--ocean-deep)]">Need something custom?</h3>
          <p className="text-sm text-[var(--text-secondary)] mt-2">For 10+ users, custom integrations, or non-profit/artisan-collective rates — get in touch.</p>
          <a href={`mailto:${SUPPORT_EMAIL}?subject=Custom%20SAIL%20plan`} className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-br from-[var(--ocean-surface)] to-[var(--ocean-deep)] text-white font-semibold">
            <Mail className="w-4 h-4"/> Email us
          </a>
        </div>
      </div>
    </div>
  );
}
